import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Search, FileText, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useTaxonomy } from "../../lib/useTaxonomy";

const inputCls = "bg-[#0F0F0F] border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#D4AF37]";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DOC_TYPES = [
  { key: "aadhar", label: "Aadhar" },
  { key: "birth_certificate", label: "Birth Certificate" },
  { key: "school_id", label: "School ID" },
];

export default function Documents() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [centre, setCentre] = useState("");
  const [batch, setBatch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { centreNames, batchNames } = useTaxonomy();

  const load = () => api.get("/admin/documents/matrix").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (playerId, docKey, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/documents/${playerId}/${docKey}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = rows.filter((r) => {
    if (centre && r.centre !== centre) return false;
    if (batch && r.batch !== batch) return false;
    if (search && !`${r.player_id} ${r.name}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter === "complete" && !(r.aadhar && r.birth_certificate && r.school_id)) return false;
    if (statusFilter === "incomplete" && (r.aadhar && r.birth_certificate && r.school_id)) return false;
    return true;
  });

  const stats = DOC_TYPES.map((d) => ({ label: d.label, key: d.key, count: rows.filter((r) => r[d.key]).length, total: rows.length }));

  return (
    <div data-testid="admin-documents">
      <h1 className="font-heading text-3xl font-black uppercase tracking-tight mb-6">Documents</h1>

      <div className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-6" data-testid="doc-stats">
        {stats.map((s) => (
          <div key={s.key} className="bg-[#0A0A0A] p-5" data-testid={`doc-stat-${s.key}`}>
            <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">{s.label}</p>
            <p className="font-heading text-3xl font-black text-[#D4AF37]">{s.count}<span className="text-white/40 text-lg">/{s.total}</span></p>
            <p className="text-white/40 text-xs mt-1">{s.total ? Math.round(s.count / s.total * 100) : 0}% uploaded</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input placeholder="Search player…" value={search} onChange={(e) => setSearch(e.target.value)} className={inputCls + " pl-9 w-60"} data-testid="doc-search" />
        </div>
        <select value={centre} onChange={(e) => setCentre(e.target.value)} className={inputCls} data-testid="doc-centre">
          <option value="">All Centres</option>
          {centreNames.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={batch} onChange={(e) => setBatch(e.target.value)} className={inputCls} data-testid="doc-batch">
          <option value="">All Batches</option>
          {batchNames.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <div className="flex border border-white/10 rounded-sm overflow-hidden">
          {[["all", "All"], ["incomplete", "Missing docs"], ["complete", "Complete"]].map(([v, label]) => (
            <button key={v} onClick={() => setStatusFilter(v)}
              className={`text-xs uppercase tracking-widest px-4 py-2.5 transition-colors duration-150 ${statusFilter === v ? "bg-[#D4AF37] text-black" : "text-white/60 hover:text-white"}`}
              data-testid={`doc-filter-${v}`}>{label}</button>
          ))}
        </div>
      </div>

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/10">
              <th className="p-4">Player</th>
              <th className="p-4 hidden md:table-cell">Batch</th>
              {DOC_TYPES.map((d) => <th key={d.key} className="p-4 text-center">{d.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.player_id} className="border-b border-white/5" data-testid={`doc-row-${r.player_id}`}>
                <td className="p-4"><p className="font-semibold">{r.name}</p><p className="text-[#D4AF37] text-xs">{r.player_id}</p></td>
                <td className="p-4 text-white/60 hidden md:table-cell">{r.batch || "—"}</td>
                {DOC_TYPES.map((d) => (
                  <td key={d.key} className="p-4 text-center" data-testid={`doc-cell-${r.player_id}-${d.key}`}>
                    <div className="flex items-center justify-center gap-2">
                      {r[d.key] ? (
                        <a href={`${BACKEND_URL}/api/documents/${r.player_id}/${d.key}`} target="_blank" rel="noreferrer" className="text-[#D4AF37]" title="View">
                          <CheckCircle2 size={18} />
                        </a>
                      ) : (
                        <XCircle size={18} className="text-white/25" />
                      )}
                      <label className="cursor-pointer text-[10px] uppercase tracking-widest text-white/50 hover:text-[#D4AF37]">
                        <Upload size={12} />
                        <input type="file" accept="image/*,application/pdf" onChange={(e) => upload(r.player_id, d.key, e.target.files?.[0])} className="hidden" data-testid={`doc-upload-${r.player_id}-${d.key}`} />
                      </label>
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-white/40">No players match the current filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
